import router from "next/router";
import { snapshot } from "valtio";

import { checkForApiErrorCode } from "common/middleware/api.errorCodes";
import { ApiException } from "common/middleware/api.services";
import { Logger } from "common/middleware/logger";
import store from "common/middleware/store";

export class ApiError {
  constructor(public errorCode: string) {}
}

export class ApiValidationError {
  constructor(public errors: { [key: string]: string[] }) {}
}

export async function callApi<T>(
  cb: Promise<T>,
  opt?: {
    errorIdentifiers?: undefined | null,
    redirectTo404?: boolean,
  },
): Promise<{ data: T, error?: ApiException | ApiValidationError }>;
export async function callApi<T>(
  cb: Promise<T>,
  opt?: {
    errorIdentifiers?: string[],
    redirectTo404?: boolean,
  },
): Promise<{ data: T, error?: ApiException | ApiError| ApiValidationError }>;
export async function callApi<T>(
  cb: Promise<T>,
  opt?: {
    errorIdentifiers?: string[],
    redirectTo404?: boolean,
  },
): Promise<{ data: T, error?: ApiException | ApiError | ApiValidationError }> {
  try {
    const data = await cb;
    return { data, error: undefined };
  } catch (error: unknown) {
    if (error instanceof ApiException) {
      // @ts-ignore - Type of ApiException is incorrect. Type of response is not always string. Would require backend fix generating api.services.ts.
      if (error.response?.title === "One or more validation errors occurred.") {
        // @ts-ignore - Type of ApiException is incorrect. Type of response is not always string. Would require backend fix generating api.services.ts.
        return { data: undefined, error: new ApiValidationError(error.response.errors) };
      }

      if (opt?.errorIdentifiers?.length > 0) {
        for (const identifier of opt?.errorIdentifiers) {
          // @ts-ignore - Type of ApiException is incorrect. Type of response is not always string. Would require backend fix generating api.services.ts.
          if (error.response?.errorIdentifier === identifier) {
            return { data: undefined, error: new ApiError(identifier) };
          }
        }
      }

      if (error.status === 404 && opt?.redirectTo404) {
        router.replace("/404");
        return { data: undefined, error: undefined };
      }

      return { data: undefined, error };
    }

    throw error;
  }
}

async function callApiHandleApiException<TReturn, TException>(
  invoke: () => TReturn,
  handleError: (exception) => TException
) {
  try {
    return await invoke();
  } catch (exception) {
    if (exception instanceof ApiException) {
      return handleError(exception);
    }
    throw exception;
  }
}

export const callApiAndHandleApiException = async <TReturn>(
  invoke: () => TReturn,
  swallowException = false
): Promise<TReturn> => {
  const result = await callApiHandleApiException(invoke, (exception) => {
    Logger.error(
      exception,
      "Error occured on API, redirecting user to error page"
    );

    if (!swallowException) {
      return undefined;
    }
    router.push("/checkout/checkout-error");
  });

  return result;
};

export const callApiAndHandleExpectedApiExceptions = async <TReturn>(
  invoke: () => TReturn,
  expectedErrorIdentifiers: Array<string>
): Promise<TReturn | ApiError | ApiValidationError> => {
  const result = await callApiHandleApiException(invoke, (exception) => {
    Logger.debug(
      "Failed to call API, proceeding to check if it's an expected error"
    );

    if (
      exception.response?.title === "One or more validation errors occurred."
    ) {
      return new ApiValidationError(exception.response.errors);
    }

    // this is for errors we expect from the API like invalid credentials, verify token mismatch
    for (let errorCode of expectedErrorIdentifiers) {
      const result = checkForApiErrorCode(exception, errorCode);

      if (result) {
        return new ApiError(errorCode);
      }
    }

    Logger.error(exception, "Error not expected, throwing exception");

    throw exception;
  });

  return result;
};

export const getUserIdOrThrow = (): string => {
  let userId = snapshot(store).userId;
  if (!userId) {
    throw new Error("There is no user id");
  }

  return userId;
};

export const getPurchaseOfferIdOrThrow = (): string => {
  let purchaseOfferId = snapshot(store).purchaseOfferId;
  if (!purchaseOfferId) {
    throw new Error("There is no purchase offer currently pending");
  }

  return purchaseOfferId;
};

export const getPurchaseOfferIdOrNull = (): string => {
  let purchaseOfferId = snapshot(store).purchaseOfferId;
  if (!purchaseOfferId) {
    return null;
  }

  return purchaseOfferId;
};
